
import { FileText, FileSearch, Clipboard, Camera, Database, CloudLightning, Brain, CheckCircle } from 'lucide-react';
import { Progress } from "@/components/ui/progress";
import { PhaseLayout } from "./PhaseLayout";

export const PhaseOne = () => {
  return (
    <PhaseLayout 
      order={1}
      icon={<FileText className="text-white w-8 h-8" />}
      title="Phase 1: Automated Data Collection"
      description="Wamy seamlessly gathers essential documentation from all parties involved in the claim process."
      steps={[
        {
          number: "1",
          title: "From Property Owners",
          description: "Loss dates, insurance declarations, maintenance records"
        },
        {
          number: "2",
          title: "From Contractors & Adjusters",
          description: "Inspection reports, damage photos, repair estimates"
        },
        {
          number: "3",
          title: "Third-Party Sources",
          description: "Historical imagery, permit data, weather information"
        }
      ]}
      contentRight={<DataCollectionIllustration />}
    />
  );
};

const DataCollectionIllustration = () => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 transform transition-transform hover:scale-105">
      <div className="bg-gray-50 rounded-lg p-6 relative">
        <h4 className="text-lg font-medium text-gray-800 mb-5">Automated Document Collection</h4>
        
        <div className="space-y-5 mb-6">
          {/* Property Owner Documents */}
          <div className="bg-white rounded-lg p-3 border border-blue-100">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mr-3">
                <FileText className="text-blue-600 w-5 h-5" />
              </div>
              <div>
                <h5 className="font-medium text-sm text-gray-800">Property Owner Documents</h5>
                <p className="text-xs text-gray-500">3 files uploaded</p>
              </div>
              <div className="ml-auto">
                <div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center">
                  <CheckCircle className="text-green-500 w-4 h-4" />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-blue-50 rounded p-2 text-center">
                <div className="w-8 h-8 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-1">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-[10px] text-blue-700">Insurance</span>
              </div>
              <div className="bg-blue-50 rounded p-2 text-center">
                <div className="w-8 h-8 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-1">
                  <Clipboard className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-[10px] text-blue-700">Loss Date</span>
              </div>
              <div className="bg-blue-50 rounded p-2 text-center">
                <div className="w-8 h-8 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-1">
                  <FileSearch className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-[10px] text-blue-700">Records</span>
              </div>
            </div>
          </div>
          
          {/* Contractor & Adjuster Documents */}
          <div className="bg-white rounded-lg p-3 border border-green-100">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mr-3">
                <Clipboard className="text-green-600 w-5 h-5" />
              </div>
              <div>
                <h5 className="font-medium text-sm text-gray-800">Contractor & Adjuster Files</h5>
                <p className="text-xs text-gray-500">5 files uploaded</p>
              </div>
              <div className="ml-auto">
                <div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center">
                  <CheckCircle className="text-green-500 w-4 h-4" />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-green-50 rounded p-2 text-center">
                <div className="w-8 h-8 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-1">
                  <FileSearch className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-[10px] text-green-700">Report</span>
              </div>
              <div className="bg-green-50 rounded p-2 text-center">
                <div className="w-8 h-8 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-1">
                  <Camera className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-[10px] text-green-700">Photos</span>
              </div>
              <div className="bg-green-50 rounded p-2 text-center">
                <div className="w-8 h-8 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-1">
                  <Clipboard className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-[10px] text-green-700">Estimate</span>
              </div>
            </div>
          </div>
          
          {/* Third-Party Data */}
          <div className="bg-white rounded-lg p-3 border border-purple-100">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center mr-3">
                <Database className="text-purple-600 w-5 h-5" />
              </div>
              <div>
                <h5 className="font-medium text-sm text-gray-800">Third-Party Sources</h5>
                <p className="text-xs text-gray-500">3 data sources connected</p>
              </div>
              <div className="ml-auto">
                <div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center">
                  <CheckCircle className="text-green-500 w-4 h-4" />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-purple-50 rounded p-2 text-center">
                <div className="w-8 h-8 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-1">
                  <CloudLightning className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-[10px] text-purple-700">Weather</span>
              </div>
              <div className="bg-purple-50 rounded p-2 text-center">
                <div className="w-8 h-8 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-1">
                  <Camera className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-[10px] text-purple-700">Imagery</span>
              </div>
              <div className="bg-purple-50 rounded p-2 text-center">
                <div className="w-8 h-8 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-1">
                  <FileSearch className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-[10px] text-purple-700">Permits</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Data completion</span>
          <span className="text-sm font-medium">82%</span>
        </div>
        <Progress value={82} className="h-2 bg-gray-200" />
        
        <div className="mt-4 bg-blue-50 rounded-lg p-3 flex items-center">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
            <Brain className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-blue-700">
              AI automatically organizing & categorizing all collected documentation
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
